/**
 * Checklist de implantação — o mapa de uma empresa que está entrando.
 *
 * A pergunta que a tela responde é a de quem assume uma empresa que **já tem PCMSO e PGR**
 * feitos por outro prestador: o que precisa entrar no sistema para dar continuidade. A cadeia
 * aparece na ordem em que precisa ser cumprida, cada item diz o que foi encontrado, e o botão
 * leva à tela que resolve.
 *
 * O detalhe fino da emissão continua na prévia do documento, que lista pendência por pendência.
 * Aqui é o passo anterior: o cadastro que precisa existir antes de a emissão ter o que validar.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  ErrorOutline as PendenteIcon,
  WarningAmber as AtencaoIcon,
  ArrowForward as IrIcon,
} from '@mui/icons-material';

import { PageShell } from '../../components';
import { ActionButton } from '../../components/common';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { companyService } from '../../services/companyService';
import implantacaoService from '../../services/implantacaoService';
import useCompanyContext from '../../hooks/useCompanyContext';
import type {
  ChecklistDaImplantacao,
  ItemDaImplantacao,
  SituacaoDoItem,
} from '../../types/implantacao';

interface EmpresaDaLista {
  id: number;
  name: string;
}

/** A carga fica fora do componente para não trocar de identidade a cada render. */
const carregarEmpresas = async (): Promise<readonly EmpresaDaLista[]> =>
  companyService.getCompaniesForDropdown();

const ROTULO_DA_SITUACAO: Record<SituacaoDoItem, string> = {
  ok: 'Pronto',
  atencao: 'Sai com lacuna',
  pendente: 'Falta',
};

export const ImplantacaoPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { shouldShowCompanyField, effectiveCompanyId } = useCompanyContext();

  const [empresaId, setEmpresaId] = useState<string>(
    effectiveCompanyId ? String(effectiveCompanyId) : ''
  );
  const [checklist, setChecklist] = useState<ChecklistDaImplantacao | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const empresas = useListaPermitida<EmpresaDaLista>('company.read', 'Empresas', carregarEmpresas);

  const carregar = useCallback(async () => {
    if (!empresaId) {
      setChecklist(null);
      return;
    }

    setCarregando(true);
    setErro(null);
    try {
      setChecklist(await implantacaoService.checklist(empresaId));
    } catch (e) {
      setChecklist(null);
      setErro(e instanceof Error ? e.message : 'Não foi possível montar o checklist.');
    } finally {
      setCarregando(false);
    }
  }, [empresaId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const corDaSituacao = (situacao: SituacaoDoItem): string => {
    if (situacao === 'ok') return theme.palette.success.main;

    return situacao === 'atencao' ? theme.palette.warning.main : theme.palette.error.main;
  };

  const iconeDaSituacao = (situacao: SituacaoDoItem): React.ReactElement => {
    if (situacao === 'ok') return <OkIcon sx={{ color: theme.palette.success.main }} />;
    if (situacao === 'atencao') return <AtencaoIcon sx={{ color: theme.palette.warning.main }} />;

    return <PendenteIcon sx={{ color: theme.palette.error.main }} />;
  };

  const linhaDoItem = (item: ItemDaImplantacao): React.ReactElement => (
    <Box
      key={item.chave}
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        py: 1.5,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ pt: 0.25 }}>{iconeDaSituacao(item.situacao)}</Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {item.titulo}
          </Typography>
          <Chip
            size="small"
            label={`${ROTULO_DA_SITUACAO[item.situacao]} — ${item.detalhe}`}
            sx={{
              color: corDaSituacao(item.situacao),
              borderColor: corDaSituacao(item.situacao),
            }}
            variant="outlined"
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {item.descricao}
        </Typography>
      </Box>

      {item.caminho && (
        // A permissão é a da TELA DE DESTINO, que o backend manda junto — perguntar por
        // `company.read`, a de quem abriu o checklist, mandava para uma recusa quem não
        // alcança o destino: aplicação de risco e de exame exigem perspectiva Plataforma.
        <ActionButton
          acao={item.permissao ?? 'company.read'}
          rotulo={`Abrir ${item.titulo}`}
          size="small"
          endIcon={<IrIcon />}
          onClick={() => navigate(item.caminho ?? '/')}
        >
          Abrir
        </ActionButton>
      )}
    </Box>
  );

  const progresso = checklist ? Math.round((checklist.resolvidos / checklist.total) * 100) : 0;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Implantação' }]}
      title="Checklist de implantação"
      subtitle="O que já está no sistema e o que falta para esta empresa ser atendida"
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      {shouldShowCompanyField && (
        <TextField
          select
          label="Empresa"
          value={empresaId}
          onChange={(evento) => setEmpresaId(evento.target.value)}
          sx={{ mb: 3, minWidth: 320 }}
          disabled={!empresas.permitido}
          helperText={empresas.bloqueio}
        >
          {empresas.itens.map((empresa) => (
            <MenuItem key={empresa.id} value={String(empresa.id)}>
              {empresa.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      {carregando && <LinearProgress sx={{ mb: 2 }} />}

      {!empresaId && !carregando && (
        <Typography variant="body2" color="text.secondary">
          Escolha uma empresa para ver o que falta na implantação dela.
        </Typography>
      )}

      {checklist && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {checklist.empresa}
                </Typography>
                <Chip
                  size="small"
                  color={checklist.pendentes === 0 ? 'success' : 'default'}
                  label={`${String(checklist.resolvidos)} de ${String(checklist.total)} prontos`}
                />
                {checklist.pendentes > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${String(checklist.pendentes)} a cadastrar`}
                  />
                )}
                {checklist.bloqueiamEmissao > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    label={`${String(checklist.bloqueiamEmissao)} impedindo a emissão`}
                  />
                )}
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progresso}
                sx={{ mt: 2, height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary">
                {checklist.bloqueiamEmissao > 0
                  ? 'Enquanto houver item em vermelho, PGR e PCMSO não são gerados.'
                  : checklist.pendentes === 0
                    ? 'O cadastro está completo — a prévia do documento dirá se falta algo na emissão.'
                    : 'Dá para emitir para testar: o que falta sai como aviso na prévia do documento, não como recusa.'}
              </Typography>
            </CardContent>
          </Card>

          <Stack spacing={3}>
            {checklist.etapas.map((etapa) => (
              <Card key={etapa.chave}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {etapa.titulo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {etapa.descricao}
                  </Typography>
                  {etapa.itens.map((item) => linhaDoItem(item))}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}
    </PageShell>
  );
};

export default ImplantacaoPage;
